import { Factory, GridView, Search } from '@mui/icons-material';
import { Box, SelectChangeEvent } from '@mui/material';
import React, { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRoomComponentsData } from '../../provider/RoomComponentsDataProvider';
import { PRODUCT_GALLERY_INPUT_SEARCH_DELAY } from '../../utils/constants';
import { searchInFetchedData, searchInFetchedModels2D } from '../../utils/searchUtils';
import ImsSelect from './ImsSelect';
import ImsTextField from './ImsTextField';

export interface TextureSearchPanelProps {
  selectedTab: number;
  items: any[]; // For selectedTab 0: product textures; 1: materials; 2: colors.
  returnResults: (results: any[]) => void;
}

const TextureSearchPanel = ({ selectedTab, items, returnResults }: TextureSearchPanelProps): JSX.Element => {
  const intl = useIntl();
  const { setIsUserSearching } = useRoomComponentsData();

  // Manufacturer filter is shown only for product textures.
  const showManufacturerFilter = selectedTab === 0;
  // Category filter is shown for product textures and materials.
  const showCategoryFilter = selectedTab === 0 || selectedTab === 1;

  const allManufacturersLabel = intl.formatMessage({
    id: 'ProductGallery.allManufacturers',
    defaultMessage: 'All Manufacturers',
  });
  const allCategoriesLabel = intl.formatMessage({
    id: 'ProductGallery.allCategories',
    defaultMessage: 'All Categories',
  });

  const [filteredManufacturers, setFilteredManufacturers] = useState<string[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<string[]>([]);
  const [searchString, setSearchString] = useState<string>('');
  const [searchResults, setSearchResults] = useState<any[]>([]);

  const searchTimerRef = useRef<number | null>(null);

  const manufacturers = useMemo(() => {
    if (showManufacturerFilter && Array.isArray(items)) {
      const names = Array.from(
        new Set(items.map((item: any) => item.manufacturer?.name).filter((name: string | undefined): name is string => !!name)),
      );
      return names.length > 0 ? names : [allManufacturersLabel];
    }
    return [];
  }, [items, showManufacturerFilter, allManufacturersLabel]);

  const categories = useMemo(() => {
    if (showCategoryFilter && Array.isArray(items)) {
      const cats = new Set<string>();
      items.forEach((item: any) => {
        if (selectedTab === 0) {
          // Texture products: assume objects[] (like FilesAPIProductModel)
          if (item.objects && Array.isArray(item.objects)) {
            item.objects.forEach((obj: any) => {
              if (obj.name) {
                cats.add(obj.name);
              }
            });
          }
        } else if (selectedTab === 1) {
          // Materials: use object.name (like IFilesApiMaterial)
          if (item.object?.name) {
            cats.add(item.object.name);
          }
        }
      });
      return cats.size > 0 ? Array.from(cats) : [allCategoriesLabel];
    }
    return [];
  }, [items, showCategoryFilter, selectedTab, allCategoriesLabel]);

  const isSearching = useMemo(() => {
    const hasManufacturerFilter =
      filteredManufacturers.length > 0 && !(filteredManufacturers.length === 1 && filteredManufacturers[0] === allManufacturersLabel);
    const hasCategoryFilter =
      filteredCategories.length > 0 && !(filteredCategories.length === 1 && filteredCategories[0] === allCategoriesLabel);
    return searchString.trim() !== '' || hasManufacturerFilter || hasCategoryFilter;
  }, [searchString, filteredManufacturers, filteredCategories, allManufacturersLabel, allCategoriesLabel]);

  const triggerSearch = () => {
    if (isSearching) {
      setIsUserSearching(true);
      if (selectedTab === 0) {
        // Texture products: assume objects[] for categories
        const results = searchInFetchedData(
          { products: items },
          searchString,
          filteredManufacturers.length > 0 ? filteredManufacturers : [allManufacturersLabel],
          filteredCategories.length > 0 ? filteredCategories : [allCategoriesLabel],
          allManufacturersLabel,
          allCategoriesLabel,
        );
        setSearchResults(results);
      } else if (selectedTab === 1) {
        // Materials: use object.name for categories
        const results = searchInFetchedModels2D(
          items,
          searchString,
          filteredCategories.length > 0 ? filteredCategories : [allCategoriesLabel],
          allCategoriesLabel,
        );
        setSearchResults(results);
      } else {
        // Colors: filter by name only
        const lowerSearch = searchString.toLowerCase();
        const results = (items as any[]).filter((item) => item.name.toLowerCase().includes(lowerSearch));
        setSearchResults(results);
      }
    } else {
      setSearchResults([]);
      setIsUserSearching(false);
    }
  };

  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = window.setTimeout(() => {
      triggerSearch();
    }, PRODUCT_GALLERY_INPUT_SEARCH_DELAY);
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchString, filteredManufacturers, filteredCategories, selectedTab]);

  useEffect(() => {
    setSearchString('');
    setFilteredManufacturers([]);
    setFilteredCategories([]);
    setSearchResults([]);
    returnResults([]);
    setIsUserSearching(false);
  }, [selectedTab]);

  useEffect(() => () => {
    setIsUserSearching(false);
  }, []);

  useEffect(() => {
    returnResults(searchResults);
  }, [searchResults, returnResults]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchString(e.target.value);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter') {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
      triggerSearch();
    }
  };

  const handleManufacturerChange = (e: SelectChangeEvent<string[]>): void => {
    let newSelection = e.target.value;
    if (typeof newSelection === 'string') {
      newSelection = newSelection.split(',');
    }
    if (newSelection.includes(allManufacturersLabel)) {
      newSelection = [allManufacturersLabel];
    }
    setFilteredManufacturers(newSelection);
  };

  const handleCategoryChange = (e: SelectChangeEvent<string[]>): void => {
    let newSelection = e.target.value;
    if (typeof newSelection === 'string') {
      newSelection = newSelection.split(',');
    }
    if (newSelection.includes(allCategoriesLabel)) {
      newSelection = [allCategoriesLabel];
    }
    setFilteredCategories(newSelection);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '95%', mx: 'auto', mt: 1 }}>
      {showCategoryFilter && (
        <ImsSelect
          multiple
          options={categories}
          value={filteredCategories}
          onChange={handleCategoryChange}
          placeholderId="RoomComponentsSearchPanel.categories"
          icon={GridView}
        />
      )}
      {showManufacturerFilter && (
        <ImsSelect
          multiple
          options={manufacturers}
          value={filteredManufacturers}
          onChange={handleManufacturerChange}
          placeholderId="RoomComponentsSearchPanel.filterManufacturers"
          icon={Factory}
        />
      )}
      <ImsTextField
        value={searchString}
        onChange={handleInputChange}
        onKeyDown={handleKeyPress}
        placeholderId="RoomComponentsSearchPanel.search"
        icon={<Search />}
      />
    </Box>
  );
};

export default TextureSearchPanel;